import {
  CustomersReportRequest,
  CustomersReportResponse,
  dispatch,
  ProductListResponse,
  ProductsRequest,
  updateCustomerProfileDownloadStatus,
} from '@zonofi/zono-money-store';
import { RequestPriority } from '../queue/request/request-queue';
import { customApiQueue } from '../queue/request';
import {
  catalogInFlightRequests,
  profileInFlightRequests,
} from '../cache/in-flight-requests';

/**
 * Wraps the RTK mutation call inside the queue.
 */
export function createCustomerProfileEnqueuer(
  getFieldOpsCustomerProfileDetails: any
) {
  return function enqueueCustomerProfile(
    payload: CustomersReportRequest,
    priority: RequestPriority = RequestPriority.LOW,
    id?: string
  ): Promise<CustomersReportResponse[]> {
    if (id && profileInFlightRequests.has(id)) {
      const existing = profileInFlightRequests.get(id)!;

      if (existing.priority < priority) {
        customApiQueue.cancelTaskById(id); // cancel queued low-priority task
      } else {
        // reuse only if same or higher priority
        return existing.promise;
      }
    }

    const promise = customApiQueue.enqueueRequest(
      () => getFieldOpsCustomerProfileDetails(payload).unwrap(),
      { priority, id }
    ) as Promise<CustomersReportResponse[]>;

    if (id) {
      profileInFlightRequests.set(id, { promise, priority });

      promise
        .then(() => {
          dispatch(
            updateCustomerProfileDownloadStatus({
              customerId: id,
              status: true,
            })
          );
        })
        .finally(() => {
          profileInFlightRequests.delete(id);
        });
    }

    return promise;
  };
}

export const createCatalogListEnqueuer =
  (getCatalogList: any) =>
  (
    payload: ProductsRequest,
    priority: RequestPriority = RequestPriority.LOW
  ): Promise<ProductListResponse> => {
    const customerId = payload.params.customerId;
    const requestId = customerId ? `catalog-${customerId}` : undefined;

    if (requestId && catalogInFlightRequests.has(requestId)) {
      const existing = catalogInFlightRequests.get(requestId)!;

      if (existing.priority < priority) {
        customApiQueue.cancelTaskById(requestId);
      } else {
        return existing.promise;
      }
    }

    const promise = customApiQueue.enqueueRequest(
      () => getCatalogList(payload).unwrap(),
      {
        priority,
        id: requestId,
      }
    ) as Promise<ProductListResponse>;

    if (requestId) {
      catalogInFlightRequests.set(requestId, { promise, priority });

      promise.finally(() => {
        catalogInFlightRequests.delete(requestId);
      });
    }

    return promise;
  };
